/**
 * API client for scheduling-related endpoints
 * Used by Technician Calendar to fetch location business hours, holidays,
 * employee schedules, and schedule exceptions
 */

import { get, post, put, destroy } from "../../../../api/axios-client";
import type {
  LocationBusinessHours,
  LocationHoliday,
  EmployeeSchedule,
  EmployeeScheduleException,
} from "../models";

const controllerUrl = "api/Scheduling/";

const SchedulingAPI = {
  createLocationBusinessHours: async (
    hours: Omit<LocationBusinessHours, "storeHoursId">,
  ): Promise<LocationBusinessHours> => {
    try {
      const { data } = await post(
        `${controllerUrl}CreateLocationBusinessHours`,
        hours,
      );
      return data.content || data.Content || data.Data || data.data || data;
    } catch (error: any) {
      console.error("Error creating location business hours:", error);
      throw error;
    }
  },

  updateLocationBusinessHours: async (
    storeHoursId: number,
    hours: Partial<LocationBusinessHours>,
  ): Promise<LocationBusinessHours> => {
    try {
      const { data } = await put(
        `${controllerUrl}UpdateLocationBusinessHours/${storeHoursId}`,
        hours,
      );
      return data.content || data.Content || data.Data || data.data || data;
    } catch (error: any) {
      console.error("Error updating location business hours:", error);
      throw error;
    }
  },

  createLocationHoliday: async (
    holiday: Omit<LocationHoliday, "storeHolidaysId">,
  ): Promise<LocationHoliday> => {
    try {
      const { data } = await post(
        `${controllerUrl}CreateLocationHoliday`,
        holiday,
      );
      return data.content || data.Content || data.Data || data.data || data;
    } catch (error: any) {
      console.error("Error creating location holiday:", error);
      throw error;
    }
  },

  updateLocationHoliday: async (
    storeHolidaysId: number,
    holiday: Partial<LocationHoliday>,
  ): Promise<LocationHoliday> => {
    try {
      const { data } = await put(
        `${controllerUrl}UpdateLocationHoliday/${storeHolidaysId}`,
        holiday,
      );
      return data.content || data.Content || data.Data || data.data || data;
    } catch (error: any) {
      console.error("Error updating location holiday:", error);
      throw error;
    }
  },

  createEmployeeSchedule: async (
    schedule: Omit<EmployeeSchedule, "empHoursId">,
  ): Promise<EmployeeSchedule> => {
    try {
      const { data } = await post(
        `${controllerUrl}CreateEmployeeSchedule`,
        schedule,
      );
      return data.content || data.Content || data.Data || data.data || data;
    } catch (error: any) {
      console.error("Error creating employee schedule:", error);
      throw error;
    }
  },

  updateEmployeeSchedule: async (
    empHoursId: number,
    schedule: Partial<EmployeeSchedule>,
  ): Promise<EmployeeSchedule> => {
    try {
      const { data } = await put(
        `${controllerUrl}UpdateEmployeeSchedule/${empHoursId}`,
        schedule,
      );
      return data.content || data.Content || data.Data || data.data || data;
    } catch (error: any) {
      console.error("Error updating employee schedule:", error);
      throw error;
    }
  },

  createEmployeeScheduleException: async (
    exception: Omit<EmployeeScheduleException, "empHoursExceptionsId">,
  ): Promise<EmployeeScheduleException> => {
    try {
      const { data } = await post(
        `${controllerUrl}CreateEmployeeScheduleException`,
        exception,
      );
      return data.content || data.Content || data.Data || data.data || data;
    } catch (error: any) {
      console.error("Error creating employee schedule exception:", error);
      throw error;
    }
  },

  updateEmployeeScheduleException: async (
    empHoursExceptionsId: number,
    exception: Partial<EmployeeScheduleException>,
  ): Promise<EmployeeScheduleException> => {
    try {
      const { data } = await put(
        `${controllerUrl}UpdateEmployeeScheduleException/${empHoursExceptionsId}`,
        exception,
      );
      return data.content || data.Content || data.Data || data.data || data;
    } catch (error: any) {
      console.error("Error updating employee schedule exception:", error);
      throw error;
    }
  },

  // Work Orders with Segments
  getWorkOrdersWithSegments: async (
    locationIds?: number[],
    searchString?: string,
    pageNumber: number = 1,
    pageSize: number = 15,
    woStatusIds?: number[],
    documentStatuses?: string[],
    segmentSchedulingStatuses?: string[],
  ): Promise<{
    workOrders: any[];
    totalCount: number;
    pageNumber: number;
    pageSize: number;
    totalPages: number;
  }> => {
    try {
      const { data } = await post(`${controllerUrl}GetWorkOrdersWithSegments`, {
        locationIds: locationIds || [],
        searchString: searchString || "",
        pageNumber,
        pageSize,
        woStatusIds: woStatusIds || [],
        documentStatuses:
          documentStatuses && documentStatuses.length > 0
            ? documentStatuses
            : [],
        segmentSchedulingStatuses: segmentSchedulingStatuses || [],
      });
      const response =
        data.content || data.Content || data.Data || data.data || data;
      const result = {
        workOrders: response.workOrders || response.WorkOrders || [],
        totalCount: response.totalCount || response.TotalCount || 0,
        pageNumber: response.pageNumber || response.PageNumber || pageNumber,
        pageSize: response.pageSize || response.PageSize || pageSize,
        totalPages:
          response.totalPages ||
          response.TotalPages ||
          Math.ceil(
            (response.totalCount || response.TotalCount || 0) /
              (response.pageSize || response.PageSize || pageSize),
          ),
      };
      return result;
    } catch (error: any) {
      console.error("Error fetching work orders with segments:", error);
      throw error;
    }
  },

  // TechnicianScheduleItem operations
  createScheduleItem: async (request: {
    segmentId?: number;
    segmentPartId?: number;
    workOrderId: number;
    technicianId: number;
    workDate: Date | string;
    sortKey: number;
    estimatedHours: number;
    overCapacity?: boolean;
    technicianOff?: boolean;
    displayText?: string;
    description?: string;
  }): Promise<number> => {
    try {
      const now = new Date();
      const { data } = await post(`${controllerUrl}CreateScheduleItem`, {
        ...request,
        // CRITICAL: Format workDate as YYYY-MM-DD in local timezone to avoid timezone shifts
        // Use local date components instead of toISOString() which converts to UTC
        workDate:
          request.workDate instanceof Date
            ? (() => {
                const d = request.workDate;
                const year = d.getFullYear();
                const month = String(d.getMonth() + 1).padStart(2, "0");
                const day = String(d.getDate()).padStart(2, "0");
                return `${year}-${month}-${day}`;
              })()
            : request.workDate,
        createdOn: now.toISOString(), // Send current date from UI
      });
      return data.content || data.Content || data.Data || data.data || data;
    } catch (error: any) {
      console.error("Error creating schedule item:", error);
      throw error;
    }
  },

  updateScheduleItem: async (request: {
    scheduleItemId: number;
    technicianId?: number;
    workDate?: Date | string;
    sortKey?: number;
    estimatedHours?: number;
    rowVersion?: number[];
  }): Promise<boolean> => {
    try {
      const now = new Date();
      const { data } = await put(`${controllerUrl}UpdateScheduleItem`, {
        ...request,
        // CRITICAL: Format workDate as YYYY-MM-DD in local timezone to avoid timezone shifts
        // Use local date components instead of toISOString() which converts to UTC
        workDate:
          request.workDate instanceof Date
            ? (() => {
                const d = request.workDate;
                const year = d.getFullYear();
                const month = String(d.getMonth() + 1).padStart(2, "0");
                const day = String(d.getDate()).padStart(2, "0");
                return `${year}-${month}-${day}`;
              })()
            : request.workDate,
        updatedOn: now.toISOString(), // Send current date from UI
      });
      return data.content || data.Content || data.Data || data.data || data;
    } catch (error: any) {
      console.error("Error updating schedule item:", error);
      throw error;
    }
  },

  removeScheduleItem: async (scheduleItemId: number): Promise<boolean> => {
    try {
      const now = new Date();
      const { data } = await post(`${controllerUrl}RemoveScheduleItem`, {
        scheduleItemId,
        updatedOn: now.toISOString(), // Send current date from UI
      });
      return data.content || data.Content || data.Data || data.data || data;
    } catch (error: any) {
      console.error("Error removing schedule item:", error);
      throw error;
    }
  },

  getScheduleItems: async (request: {
    locationIds?: number[];
    workDate?: Date | string;
    fromDate?: Date | string;
    toDate?: Date | string;
    technicianId?: number;
  }): Promise<any[]> => {
    try {
      const { data } = await post(`${controllerUrl}GetScheduleItems`, {
        ...request,
        // CRITICAL: Format workDate as YYYY-MM-DD in local timezone to avoid timezone shifts
        // Use local date components instead of toISOString() which converts to UTC
        workDate:
          request.workDate instanceof Date
            ? (() => {
                const d = request.workDate;
                const year = d.getFullYear();
                const month = String(d.getMonth() + 1).padStart(2, "0");
                const day = String(d.getDate()).padStart(2, "0");
                return `${year}-${month}-${day}`;
              })()
            : request.workDate,
        fromDate:
          request.fromDate instanceof Date
            ? (() => {
                const d = request.fromDate;
                const year = d.getFullYear();
                const month = String(d.getMonth() + 1).padStart(2, "0");
                const day = String(d.getDate()).padStart(2, "0");
                return `${year}-${month}-${day}`;
              })()
            : request.fromDate,
        toDate:
          request.toDate instanceof Date
            ? (() => {
                const d = request.toDate;
                const year = d.getFullYear();
                const month = String(d.getMonth() + 1).padStart(2, "0");
                const day = String(d.getDate()).padStart(2, "0");
                return `${year}-${month}-${day}`;
              })()
            : request.toDate,
      });
      return (
        data.content || data.Content || data.Data || data.data || data || []
      );
    } catch (error: any) {
      console.error("Error fetching schedule items:", error);
      throw error;
    }
  },

  // Get schedule items for a specific day (separate API call for Day view)
  getScheduleItemsForDay: async (request: {
    locationIds?: number[];
    workDate: Date | string;
    technicianId?: number;
  }): Promise<any[]> => {
    try {
      const { data } = await post(`${controllerUrl}GetScheduleItems`, {
        ...request,
        // Format workDate as YYYY-MM-DD in local timezone
        workDate:
          request.workDate instanceof Date
            ? (() => {
                const d = request.workDate;
                const year = d.getFullYear();
                const month = String(d.getMonth() + 1).padStart(2, "0");
                const day = String(d.getDate()).padStart(2, "0");
                return `${year}-${month}-${day}`;
              })()
            : request.workDate,
        // For day view, use workDate as both fromDate and toDate
        fromDate:
          request.workDate instanceof Date
            ? (() => {
                const d = request.workDate;
                const year = d.getFullYear();
                const month = String(d.getMonth() + 1).padStart(2, "0");
                const day = String(d.getDate()).padStart(2, "0");
                return `${year}-${month}-${day}`;
              })()
            : request.workDate,
        toDate:
          request.workDate instanceof Date
            ? (() => {
                const d = request.workDate;
                const year = d.getFullYear();
                const month = String(d.getMonth() + 1).padStart(2, "0");
                const day = String(d.getDate()).padStart(2, "0");
                return `${year}-${month}-${day}`;
              })()
            : request.workDate,
      });
      return (
        data.content || data.Content || data.Data || data.data || data || []
      );
    } catch (error: any) {
      console.error("Error fetching schedule items for day:", error);
      throw error;
    }
  },

  // Get schedule items for a month
  getScheduleItemsForMonth: async (request: {
    locationIds?: number[];
    month: Date | string; // First day of the month
    technicianId?: number;
  }): Promise<any[]> => {
    try {
      let monthStart: Date;
      let monthEnd: Date;

      if (request.month instanceof Date) {
        monthStart = new Date(
          request.month.getFullYear(),
          request.month.getMonth(),
          1,
        );
        monthEnd = new Date(
          request.month.getFullYear(),
          request.month.getMonth() + 1,
          0,
        );
      } else {
        const date = new Date(request.month);
        monthStart = new Date(date.getFullYear(), date.getMonth(), 1);
        monthEnd = new Date(date.getFullYear(), date.getMonth() + 1, 0);
      }

      const fromDateStr = `${monthStart.getFullYear()}-${String(monthStart.getMonth() + 1).padStart(2, "0")}-${String(monthStart.getDate()).padStart(2, "0")}`;
      const toDateStr = `${monthEnd.getFullYear()}-${String(monthEnd.getMonth() + 1).padStart(2, "0")}-${String(monthEnd.getDate()).padStart(2, "0")}`;

      const { data } = await post(`${controllerUrl}GetScheduleItems`, {
        locationIds: request.locationIds,
        fromDate: fromDateStr,
        toDate: toDateStr,
        technicianId: request.technicianId,
      });
      return (
        data.content || data.Content || data.Data || data.data || data || []
      );
    } catch (error: any) {
      console.error("Error fetching schedule items for month:", error);
      throw error;
    }
  },

  completeScheduleItem: async (request: {
    scheduleItemId: number;
    completedByUserId: number;
  }): Promise<boolean> => {
    try {
      const { data } = await post(`${controllerUrl}CompleteScheduleItem`, request);
      return data.content || data.Content || data.Data || data.data || data;
    } catch (error: any) {
      console.error("Error completing schedule item:", error);
      throw error;
    }
  },

  bulkCompleteScheduleItems: async (request: {
    scheduleItemIds: number[];
    completedByUserId: number;
  }): Promise<number> => {
    try {
      const { data } = await post(
        `${controllerUrl}BulkCompleteScheduleItems`,
        request,
      );
      return data.content || data.Content || data.Data || data.data || data;
    } catch (error: any) {
      console.error("Error bulk completing schedule items:", error);
      throw error;
    }
  },

  getCompletedAndArchivedItems: async (request: {
    locationIds?: number[];
    technicianIds?: number[];
    fromDate?: Date | string;
    toDate?: Date | string;
    filter?: "all" | "completed" | "archived";
  }): Promise<any[]> => {
    try {
      const formatDate = (value?: Date | string) => {
        if (!value) return undefined;
        if (typeof value === "string") return value;
        const d = value;
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
      };

      const filterMap = {
        all: 0,
        completed: 1,
        archived: 2,
      } as const;

      const { data } = await post(`${controllerUrl}GetCompletedAndArchivedItems`, {
        locationIds: request.locationIds || [],
        technicianIds: request.technicianIds || [],
        fromDate: formatDate(request.fromDate),
        toDate: formatDate(request.toDate),
        filter: filterMap[request.filter || "all"],
      });

      return (
        data.content || data.Content || data.Data || data.data || data || []
      );
    } catch (error: any) {
      console.error("Error fetching completed/archived schedule items:", error);
      throw error;
    }
  },
};

export default SchedulingAPI;
